import type { DecryptedMessage } from "./types";

const DATABASE = "jtuc-live-local-v2";
const STORE = "message-index";
const DRAFT_PREFIX = "jtuc-live:draft:";

type IndexedMessage = Readonly<{
  chatId: string;
  createdAt: string;
  id: string;
  text: string;
}>;

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE, 1);
    request.onupgradeneeded = () => {
      const database = request.result;
      const store = database.createObjectStore(STORE, { keyPath: "id" });
      store.createIndex("chatId", "chatId", { unique: false });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

function searchableText(message: DecryptedMessage): string | null {
  if (!message.plain || message.wire.retractedAt) return null;
  if (message.plain.type === "text" || message.plain.type === "system") return message.plain.text;
  if (message.plain.type === "file") return message.plain.name;
  return "Голосовое сообщение";
}

export async function indexMessages(messages: readonly DecryptedMessage[]): Promise<void> {
  if (!("indexedDB" in window) || messages.length === 0) return;
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE, "readwrite");
    const store = transaction.objectStore(STORE);
    for (const message of messages) {
      const text = searchableText(message);
      if (text) store.put({ chatId: message.wire.chatId, createdAt: message.wire.clientCreatedAt, id: message.wire.id, text } satisfies IndexedMessage);
      else store.delete(message.wire.id);
    }
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}

export async function searchIndexedMessages(query: string, limit = 100): Promise<IndexedMessage[]> {
  const normalized = query.trim().toLocaleLowerCase("ru-RU");
  if (!normalized || !("indexedDB" in window)) return [];
  const database = await openDatabase();
  const all = await new Promise<IndexedMessage[]>((resolve, reject) => {
    const request = database.transaction(STORE).objectStore(STORE).getAll();
    request.onsuccess = () => resolve(request.result as IndexedMessage[]);
    request.onerror = () => reject(request.error);
  });
  database.close();
  return all
    .filter((message) => message.text.toLocaleLowerCase("ru-RU").includes(normalized))
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
    .slice(0, limit);
}

export async function removeIndexedMessage(messageId: string): Promise<void> {
  if (!("indexedDB" in window)) return;
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE, "readwrite");
    transaction.objectStore(STORE).delete(messageId);
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}

export function loadDraft(chatId: string): string {
  return localStorage.getItem(`${DRAFT_PREFIX}${chatId}`) ?? "";
}

export function saveDraft(chatId: string, value: string): void {
  const key = `${DRAFT_PREFIX}${chatId}`;
  if (value) localStorage.setItem(key, value);
  else localStorage.removeItem(key);
}
